import { EntryType, ReservedNodeType } from '@morph-mapper/types';
import type { TreeNode } from '@morph-mapper/types';

export type EvaluableField = {
  id: string;
  key: string;
  /** What the editor shows for the field. */
  name: string;
  /** Keys of the maps above the field, from the root down (for ordering). */
  path: string[];
  filled: boolean;
  iterator: boolean;
  /** Its suggestions may be outdated, e.g. after an iterator change. */
  stale: boolean;
};

const EVALUABLE_TYPES = new Set<EntryType>([
  EntryType.Simple,
  EntryType.Graph,
  EntryType.Cell,
  EntryType.Code,
  EntryType.Boolean,
]);

const isIteratorKey = (key: unknown) =>
  String(key) === ReservedNodeType.Iterator;

const pathOf = (
  entry: TreeNode,
  entries: Record<string, TreeNode>,
): string[] => {
  const path: string[] = [];
  const seen = new Set<string>();
  let parentId = entry.parentId;
  while (parentId !== undefined && !seen.has(parentId)) {
    const parent = entries[parentId];
    // The root map has no parent and no key worth showing.
    if (!parent || parent.parentId === undefined) break;
    seen.add(parentId);
    path.unshift(String(parent.key));
    parentId = parent.parentId;
  }
  return path;
};

/**
 * Fields the plugins can evaluate: every field that holds a value of its
 * own (no maps, no internal fields), including iterators, in tree order.
 */
export const listEvaluableFields = (
  entries: Record<string, TreeNode>,
  hasValue: (id: string) => boolean,
  stale: Record<string, true> = {},
): EvaluableField[] =>
  Object.values(entries)
    .filter((entry) => EVALUABLE_TYPES.has(entry.type))
    .map((entry) => ({
      id: entry.id,
      key: String(entry.key),
      name: entry.name || String(entry.key),
      path: pathOf(entry, entries),
      filled: hasValue(entry.id),
      iterator: isIteratorKey(entry.key),
      stale: Boolean(stale[entry.id]),
    }))
    .sort(
      (a, b) =>
        a.path.join('/').localeCompare(b.path.join('/')) ||
        Number(b.iterator) - Number(a.iterator) ||
        a.key.localeCompare(b.key),
    );

/**
 * Selected by default in the re-evaluation dialog: fields without a value
 * and fields marked stale (e.g. after an iterator change). Iterators are
 * only re-detected on request.
 */
export const defaultSelection = (fields: EvaluableField[]): Set<string> =>
  new Set(
    fields
      .filter((f) => !f.iterator && (!f.filled || f.stale))
      .map((f) => f.id),
  );

/**
 * Fields that already have a value. Iterators are left out, like in the
 * default selection: selecting one runs the Iterator plugin again.
 */
export const filledSelection = (fields: EvaluableField[]): Set<string> =>
  new Set(fields.filter((f) => !f.iterator && f.filled).map((f) => f.id));

const iteratorState = (entry: TreeNode) => {
  const { type, value, graphId } = entry as TreeNode & {
    value?: unknown;
    graphId?: string;
  };
  return JSON.stringify([type, value, graphId]);
};

/**
 * The iterator per map, as text to compare ('' for a map without one).
 */
export const iteratorSnapshot = (
  entries: Record<string, TreeNode>,
): Record<string, string> => {
  const snapshot: Record<string, string> = {};
  const all = Object.values(entries);
  all.forEach((entry) => {
    if (entry.type === EntryType.Map) snapshot[entry.id] = '';
  });
  all.forEach((entry) => {
    const { parentId } = entry;
    if (isIteratorKey(entry.key) && parentId && parentId in snapshot) {
      snapshot[parentId] = iteratorState(entry);
    }
  });
  return snapshot;
};

/**
 * Maps whose iterator was set, changed or removed. Maps that only exist in
 * one snapshot (another template was loaded) do not count.
 */
export const changedIteratorMaps = (
  previous: Record<string, string>,
  next: Record<string, string>,
): string[] =>
  Object.keys(next).filter((id) => id in previous && previous[id] !== next[id]);

/**
 * The fields below the given maps, at any depth, without the iterators
 * themselves.
 */
export const fieldsBelow = (
  mapIds: string[],
  entries: Record<string, TreeNode>,
): string[] => {
  const children = new Map<string, TreeNode[]>();
  Object.values(entries).forEach((entry) => {
    if (entry.parentId === undefined) return;
    const list = children.get(entry.parentId) ?? [];
    list.push(entry);
    children.set(entry.parentId, list);
  });

  const found = new Set<string>();
  const visited = new Set<string>();
  const queue = [...mapIds];
  for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
    if (visited.has(id)) continue;
    visited.add(id);
    (children.get(id) ?? []).forEach((child) => {
      if (child.type === EntryType.Map) queue.push(child.id);
      else if (EVALUABLE_TYPES.has(child.type) && !isIteratorKey(child.key)) {
        found.add(child.id);
      }
    });
  }
  return [...found];
};
